import React, { useCallback, useEffect, useRef } from 'react'

import { unRef } from '@/helpers/shared'

/**
 * Watch for changes to the size of an element.
 *
 * @param target - React ref or DOM node
 * @param callback - callback to execute when the size of the element changes
 * @param options - Options passed to ResizeObserver
 */
export function useResizeObserver(
  target: React.RefObject<Element | null | undefined>,
  callback: ResizeObserverCallback,
  options: ResizeObserverOptions = {},
) {
  const observer = useRef<ResizeObserver | null>(null)

  const stop = useCallback(() => {
    if (!observer.current) return

    observer.current.disconnect()
    observer.current = null
  }, [])

  useEffect(() => {
    const el = unRef(target)

    if (!el) return

    observer.current = new ResizeObserver(callback)
    observer.current?.observe(el, options)

    return stop
  }, [callback, stop, options, target])

  return { stop }
}
